import { getLocalTimeZone, today } from "@internationalized/date";

/**
 * Parses a salon-entered hourly amount into the integer cents the API takes.
 *
 * Returns null for anything that is not a usable amount, so a caller can tell
 * "nothing entered yet" apart from a real zero — a zero cost is a legitimate
 * arrangement for a working proprietor, and must not share a representation
 * with an empty field.
 */
export function parseHourlyCostToCents(input: string): number | null {
  const normalized = input.trim().replace(",", ".");

  if (normalized === "") return null;
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;

  const [whole, fraction = ""] = normalized.split(".");

  // Rounded off the decimal digits rather than off `Number(normalized) * 100`:
  // 1.005 is 1.00499… in binary floating point, so multiplying first rounds it
  // down to a cent less than was typed.
  const cents =
    Number(whole) * 100 +
    Number(fraction.slice(0, 2).padEnd(2, "0")) +
    (Number(fraction[2] ?? "0") >= 5 ? 1 : 0);

  return Number.isSafeInteger(cents) ? cents : null;
}

/**
 * The `YYYY-MM-DD` a date picker takes, from whatever the API returned for a
 * DATE column — a `Date` in the declared response type, an ISO string in the
 * JSON that actually arrives.
 *
 * Reads the UTC day, which is how a DATE column round-trips. A wall-clock
 * instant does not belong here: east of UTC its UTC day is yesterday.
 */
export function toDateInputValue(
  value: Date | string | null | undefined,
): string | null {
  if (value == null || value === "") return null;

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString().slice(0, 10);
}

/**
 * The `YYYY-MM-DD` a date picker starts on when the salon has chosen nothing.
 *
 * The viewer's own day, not the UTC one: for part of every day they disagree,
 * and defaulting to the UTC day backdates a cost rate into a day that has
 * already been reported on.
 */
export function todayInputValue(): string {
  return today(getLocalTimeZone()).toString();
}
